import React from 'react';
import { TypographyProps, Typography } from '@mui/material';
import { Domain } from '@3nickels/data-modules';
import i18next, { t } from 'i18next';
import { formatDecimalDollars, formatWholeDollars } from '../../helpers/utilityFunctions';
import { getInterval } from '../../pages/settings/manage-subscription/functions';
import { Row } from '../../components';
import { useDeviceType } from '../../hooks/useDeviceType';

type PriceDisplayVariant = 'default' | 'learnMore';

interface TypographyStyle {
  variant: TypographyProps['variant'];
  color: TypographyProps['color'];
}

interface PriceDisplayStyles {
  price: TypographyStyle;
  interval: TypographyStyle;
  subtext: TypographyStyle;
}

function getPriceDisplayStyles(
  guidedChoice: boolean,
  variant: PriceDisplayVariant,
  isMobile: boolean,
  freeTrial30Day: boolean
): PriceDisplayStyles {
  if (guidedChoice)
    return {
      price: {
        variant: 'p20OpenSansSemiBold',
        color: freeTrial30Day ? 'info.main' : 'secondary.dark',
      },
      interval: { variant: 'p16OpenSans', color: 'info.main' },
      subtext: { variant: 'p12OpenSansSemiBold', color: 'secondary.dark' },
    };

  if (variant === 'learnMore' && isMobile)
    return {
      price: { variant: 'p20Bold', color: 'secondary' },
      interval: { variant: 'p16', color: 'secondary' },
      subtext: { variant: 'p12Bold', color: 'primary' },
    };

  if (variant === 'learnMore')
    return {
      price: { variant: 'p20SemiBold', color: 'secondary' },
      interval: { variant: 'h3', color: 'secondary' },
      subtext: { variant: 'p12SemiBold', color: 'primary' },
    };

  return {
    price: { variant: 'p24Bold', color: 'secondary' },
    interval: { variant: 'p16', color: 'secondary' },
    subtext: { variant: 'p12', color: 'primary' },
  };
}

interface PriceDisplayProps {
  pricebookBundle: Domain.PricebookBundle;
  variant?: PriceDisplayVariant;
  isGuidedChoice?: boolean;
  showSubtext?: boolean;
  showAsterisk?: boolean;
}

export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  pricebookBundle,
  variant = 'default',
  isGuidedChoice = false,
  showSubtext = true,
  showAsterisk = true,
}) => {
  const { isMobile } = useDeviceType();
  const { language } = i18next;
  const metadata = pricebookBundle.pricebook.metadata;
  const primaryPrice = pricebookBundle.prices[0];
  const priceInCents = primaryPrice.price;
  const priceInDollars = priceInCents / 100;
  const intervalType = primaryPrice.intervalType;

  const freeTrial30Day = metadata?.freeTrial30Day === 'true';
  const internalFreeTrial = metadata?.internalFreeTrial === 'true';
  const freemium = Boolean(metadata?.freemium);

  const styles = getPriceDisplayStyles(isGuidedChoice, variant, isMobile, freeTrial30Day);

  const hasCents = !Number.isInteger(priceInDollars);
  const basePrice = hasCents
    ? formatDecimalDollars(priceInDollars, language)
    : formatWholeDollars(priceInDollars, language);

  const priceText = freeTrial30Day
    ? t('30DaysFree')
    : `${basePrice}${showAsterisk && (freemium || internalFreeTrial) ? '*' : ''}`;

  const showInterval =
    intervalType !== Domain.PaymentIntervalEnum.NONE &&
    !freeTrial30Day &&
    !internalFreeTrial &&
    !freemium;

  function getSubtext(): string {
    if (freeTrial30Day) {
      return t('thenPricePeriod', {
        price: formatDecimalDollars(14.99, language),
        period: t('mo'),
      });
    }
    if (internalFreeTrial) return t('noSubscriptionRequired');
    if (priceInDollars === 0) return t('free');
    if (intervalType === Domain.PaymentIntervalEnum.MONTH) return t('autoRenewsMonthly');
    if (intervalType === Domain.PaymentIntervalEnum.YEAR) return t('autoRenewsAnnually');
    return t('oneTimePayment');
  }

  return (
    <>
      <Row style={{ justifyContent: 'center' }}>
        <Typography
          variant={styles.price.variant}
          color={styles.price.color}
          sx={{ alignItems: 'baseline' }}>
          {priceText}
          {showInterval && (
            <Typography
              variant={styles.interval.variant}
              color={styles.interval.color}
              sx={{ fontWeight: 'normal' }}>
              /{t(getInterval(intervalType))}
            </Typography>
          )}
        </Typography>
      </Row>
      {showSubtext && (
        <Typography
          variant={styles.subtext.variant}
          color={styles.subtext.color}
          textAlign='center'
          justifyContent='center'>
          {getSubtext()}
        </Typography>
      )}
    </>
  );
};
